import React, { FC, useEffect, useState } from "react";

import HttpStatus from "http-status-codes";
import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";

import NotAuthenticatedFormLayout from "@/components/layouts/NotAuthenticated/NotAuthenticatedFormLayout";
import { FlowSteps } from "@/components/molecules";
import { sanitizeUrl } from "@/core/security";
import { replaceUrlParams } from "@/core/string";
import { authApi, userApi } from "@/shared/apis";
import { AppRoute, UserState } from "@/shared/domain";
import { URL_EXPIRED } from "@/shared/errors";
import { useAuth, useDialog, useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import InviteDefinePassword from "./components/InviteDefinePassword/InviteDefinePassword";
import InviteIdentifyAccount from "./components/InviteIdentifyAccount/InviteIdentifyAccount";
import { InviteFromBrokerSteps, stepperData, stepperDataIdentify } from "./utils";

const InviteFromBroker: FC = () => {
    const {
        globalState: { lang, officeId },
    } = useRouterHook();

    const { token } = useParams<any>();
    const { auth, loginSilent } = useAuth();
    const { t, i18n } = useTranslation();
    const { navToRoot } = useNavigation();
    const { errorDialog } = useDialog();
    const [wrongPassword, setWrongPassword] = useState(false);
    const [password, setPassword] = useState("");

    const {
        data: userAuth = {},
        isError: isValidationTokenError,
        error: validationTokenError,
    } = useQuery(["getActivateTokenValidate", { token }], () => userApi.getActivateTokenValidate(token), {
        enabled: !!token,
        useErrorBoundary: false,
    });

    const { data: userStatus = {} } = useQuery(
        ["getUserStatus", { email: userAuth.email }],
        () => userApi.getUserStatus(userAuth.email),
        {
            enabled: !isValidationTokenError && !!userAuth,
        },
    );

    const isExistingAccount = userStatus.state === UserState.ACCESS || userStatus.state === UserState.PENDING;

    const { mutate: login } = useMutation(
        async () => {
            await auth(userStatus.email, password);
            await loginSilent(userStatus.email, officeId);
        },
        {
            onSuccess: () => {
                navToRoot();
            },
        },
    );

    const { mutate: activateComplete, isLoading: isLoadingActivateComplete } = useMutation(userApi.activateComplete, {
        onSuccess: () => {
            log.info("activate invite complete success");
            tagManager.pushSignUpEvent("email", "push");
            login();
        },
        onError: (e) => {
            //@ts-ignore
            if (isExistingAccount && e.status === HttpStatus.BAD_REQUEST) {
                setWrongPassword(true);
            } else {
                throw e;
            }
        },
    });

    useEffect(() => {
        if (isValidationTokenError) {
            //@ts-ignore
            log.warn("token validation failed", validationTokenError.message);
            errorDialog({
                //@ts-ignore
                errorCode: validationTokenError.code === "INVALID" ? URL_EXPIRED : "",
            });
            navToRoot(officeId);
        }
    }, [isValidationTokenError]);

    useEffect(() => {
        if (lang) i18n.changeLanguage(lang);

        if (!officeId || !token) {
            navToRoot(); // redirect to the login
        }
    }, []);

    const handleConfirmPassword = async (newPassword: string) => {
        setPassword(newPassword);
        setWrongPassword(false);
        activateComplete({
            body: { ...userAuth, password: newPassword },
            params: { token },
        });
    };

    const handleItsmeActivateAccount = async () => {
        // Redirect to itsme, getting the URL from the backend
        const urlWithToken = replaceUrlParams(AppRoute.ItsmeActivation, { token });
        const targetUrl = encodeURI(window.location.origin) + urlWithToken;
        const { url } = await authApi.fItsmePre(targetUrl);
        window.location.href = sanitizeUrl(url);
    };

    return (
        <NotAuthenticatedFormLayout
            officeId={officeId}
            title={t("PageTitles.InviteFromBroker")}
            flowStepsComponent={
                <FlowSteps
                    title={t("Registration.Steps.Title")}
                    availableSteps={isExistingAccount ? stepperDataIdentify : stepperData}
                    currentStep={
                        isExistingAccount
                            ? InviteFromBrokerSteps.IDENTIFY_ACCOUNT
                            : InviteFromBrokerSteps.DEFINE_PASSWORD
                    }
                />
            }
        >
            {isExistingAccount ? (
                <InviteIdentifyAccount
                    userStatus={userStatus}
                    wrongPassword={wrongPassword}
                    onIdentifyAccount={handleConfirmPassword}
                    isFetching={isLoadingActivateComplete}
                    onItsmeActivateAccount={handleItsmeActivateAccount}
                />
            ) : (
                <InviteDefinePassword
                    onItsmeActivateAccount={handleItsmeActivateAccount}
                    onDefinePassword={handleConfirmPassword}
                    isFetching={isLoadingActivateComplete}
                />
            )}
        </NotAuthenticatedFormLayout>
    );
};

export default InviteFromBroker;
